import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from 'antd'
import { AxiosError } from 'axios'
import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import ToggleMenu from '@/components/Atoms/ToggleMenu/ToggleMenu'
import BankAccounts from '@/components/OrganisationSettings/BankAccounts/BankAccounts'
import Contracts from '@/components/OrganisationSettings/Contracts/Contracts'
import Data from '@/components/OrganisationSettings/Data/Data'
import DeliveryAddresses from '@/components/OrganisationSettings/DeliveryAddresses/DeliveryAddresses'
import StorePreferences from '@/components/OrganisationSettings/StorePreferences/StorePreferences'
import Users from '@/components/OrganisationSettings/Users/Users'
import { hiddenRoles, OWNER_ROLES } from '@/constants'
import SegmentsSelect from '@/pages/OrganisationSettings/SegmentsSelect/SegmentsSelect'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { OrganisationInterface } from '@/types'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { isSupplierStaff } from '@/utils/auth/userRoles'
import { withRequiredOrganisationFields } from '@/utils/organisation/withRequiredOrganisationFields'

import {
  DEFAULT_MENU_ITEMS,
  defaultOrganisationEditSchema,
  getOrganisationEditSchema,
  OrganisationEditType,
} from './OrganisationSettings.config'
import cn from './OrganisationSettings.module.scss'

function OrganisationSettings() {
  const [currentTab, setCurrentTab] = useState('Организация')
  const [menuItems, setMenuItems] = useState(DEFAULT_MENU_ITEMS)
  const [loading] = useState(false)
  const [org, setOrg] = useState({} as OrganisationInterface)
  const [formInitialized, setFormInitialized] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const [canEditSegment, setCanEditSegment] = useState(false)

  const {
    tokenRoles,
    selectedOrganisation,
    organisation,
    bankAccountsDto,
    setSelectedOrganisation,
    setOrganisation,
    setSegmentDto,
    currentUser,
    organisationPermissions,
  } = useUserInfoStore((state) => state)

  const isOrganisationSelected =
    Object.keys(selectedOrganisation || {}).length > 0

  const navigate = useNavigate()
  const screen = useContext(ScreenSizeContext)

  const { code } = useParams()
  const orgCode = Number(code)

  const handleBack = () => {
    navigate(-1)
  }

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, dirtyFields },
    control,
    setValue,
    reset,
  } = useForm<OrganisationEditType>({
    shouldFocusError: false,
    resolver: zodResolver(
      formInitialized
        ? getOrganisationEditSchema(tokenRoles, org, organisationPermissions)
        : defaultOrganisationEditSchema
    ),
  })

  const checkCanEditSegment = () => {
    try {
      if (!Object.keys(org).length) return false

      const isAdmin = tokenRoles.includes(OWNER_ROLES.ADMIN)
      return isAdmin
    } catch (error) {
      handleApiError(error, modal)
      return false
    }
  }

  useEffect(() => {
    if (Object.keys(org).length) {
      setCanEditSegment(checkCanEditSegment())
    }
  }, [org, tokenRoles])

  useEffect(() => {
    if ([screen?.md, screen?.sm, screen?.xs].some((v) => v)) {
      navigate('/')
    }
  }, [screen, navigate])

  useEffect(() => {
    if (
      Object.keys(organisation).length &&
      orgCode == organisation.code &&
      isSupplierStaff(tokenRoles)
    ) {
      setMenuItems(DEFAULT_MENU_ITEMS)
      setCurrentTab('Организация')
    }
  }, [orgCode, organisation, tokenRoles])

  useEffect(() => {
    let counter = 0
    if (Object.keys(organisation).length && orgCode != organisation.code) {
      counter++
    }
    if (
      (Object.keys(selectedOrganisation).length &&
        orgCode != selectedOrganisation.code) ||
      (!Object.keys(selectedOrganisation).length &&
        !localStorage.getItem('selectedOrganisationCode'))
    ) {
      counter++
    }
    if (counter == 2) {
      navigate('/')
    }
  }, [orgCode, navigate, organisation, selectedOrganisation])

  useEffect(() => {
    if (!org.id) return

    setSegmentDto(null)

    const fetchOrganisationData = async () => {
      const organisationId = org.id

      try {
        const requests = [
          api
            .get(`/organisations/${organisationId}/segment`)
            .then((res) => {
              setSegmentDto(res.data.segment || res.data)
            })
            .catch(() => {
              setSegmentDto(null)
            }),
        ]

        await Promise.all(requests)
      } catch (error) {
        handleApiError(error, modal)
      }
    }

    fetchOrganisationData()
  }, [org.id])

  useEffect(() => {
    if (
      currentTab == 'Организация' &&
      (Object.keys(selectedOrganisation).length ||
        Object.keys(organisation).length)
    ) {
      let orgClone = {} as OrganisationInterface
      if (
        Object.keys(organisation).length &&
        Number(orgCode) == organisation.code
      ) {
        setOrg(organisation)
        orgClone = { ...organisation }
      } else if (
        Object.keys(selectedOrganisation).length &&
        Number(orgCode) == selectedOrganisation.code
      ) {
        setOrg(selectedOrganisation)
        orgClone = { ...selectedOrganisation }
      } else {
        return
      }

      const {
        id,
        code,
        roles,
        registrationDate,
        ipRegistrationDate,
        supervisorManagerShortModel,
        mainBankAccount,
        mainBankAccountShortModel,
        ...rest
      } = orgClone

      const formatDate = (date: string | number) => {
        if (!date) return ''
        const d = new Date(date)
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString()
      }

      reset({
        ...rest,
        supervisorManager: supervisorManagerShortModel
          ? {
              value: supervisorManagerShortModel.id,
              label: supervisorManagerShortModel.description,
            }
          : undefined,
        mainBankAccount: mainBankAccountShortModel
          ? {
              value: mainBankAccountShortModel.id,
              label: mainBankAccountShortModel.iban,
            }
          : mainBankAccount
            ? {
                value: mainBankAccount,
                label: bankAccountsDto.find(
                  (bankAccount) => bankAccount.id == mainBankAccount
                )?.iban,
              }
            : undefined,
        registrationDate: formatDate(registrationDate),
        ipRegistrationDate: formatDate(ipRegistrationDate),
      })
      setFormInitialized(true)
    }
  }, [
    currentTab,
    orgCode,
    organisation,
    selectedOrganisation,
    reset,
    bankAccountsDto,
  ])

  const onSave = (data: OrganisationEditType) => {
    const { ...rest } = data

    const loadingToast = toast.loading('Сохранение изменений...', {
      description: org.description,
    })

    api
      .put(
        `/organisations/${orgCode}`,
        withRequiredOrganisationFields(
          {
            ...rest,
            mainBankAccount: data.mainBankAccount?.value
              ? data.mainBankAccount?.value
              : null,
            isLegal: org.isLegal,
            signatoryFullName: org.signatoryFullName,
            supervisorManager: data.supervisorManager?.value
              ? data.supervisorManager.value
              : null,
            registrationDate: org.registrationDate,
            ipRegistrationDate: org.ipRegistrationDate,
          },
          org
        ),
        { _skipSanitize: true }
      )
      .then((response) => {
        const updatedOrg = response.data.organisation || response.data

        toast.success('Данные сохранены', {
          id: loadingToast,
          description: updatedOrg.description,
        })

        if (Number(orgCode) == organisation.code) {
          setOrganisation(updatedOrg)
        } else {
          setSelectedOrganisation(updatedOrg)
        }
      })
      .catch((error) => {
        toast.dismiss(loadingToast)

        if (error instanceof AxiosError) {
          if (error.response?.status == 409) {
            handleApiError(error, modal)
            return
          }
          if (
            error.response?.data.error == 'ILLEGAL_ARGUMENT' &&
            error.response?.status == 400
          ) {
            handleApiError(error, modal)
            return
          }
          toast.error('Ошибка сохранения', {
            description: getGeneralErrorMessage(error),
          })
        }
      })
    return data
  }

  return (
    <>
      {contextHolder}
      {Object.keys(org).length && !loading ? (
        <section className={cn.organisationSettings}>
          <div className={cn.inner}>
            <div className={cn.saveButtonWithTitle}>
              <div className={cn.saveButtonWithoutTitle}>
                <div className={cn.backIconContainer} onClick={handleBack}>
                  <LeftOutlined />
                </div>
                {currentTab == 'Организация' && (
                  <button
                    disabled={!Object.keys(dirtyFields).length}
                    type="submit"
                    className={
                      Object.keys(dirtyFields).length > 0
                        ? cn.canBeSubmitted
                        : cn.cantBeSubmitted
                    }
                    onClick={() => {
                      handleSubmit(onSave, () => {
                        const errorsValues = Object.keys(errors).map(
                          (key: string) =>
                            errors[key as keyof typeof errors]?.message
                        )
                        return errorsValues.join('') != '' ||
                          Object.keys(errors).length == 0
                          ? toast.error('Проверьте правильность данных', {
                              description:
                                'Некоторые поля заполнены некорректно',
                            })
                          : null
                      })()
                    }}
                  >
                    Сохранить
                  </button>
                )}
              </div>
              <div className={cn.organisationNameWithBanner}>
                <span
                  className={
                    orgCode == organisation.code
                      ? [cn.organisationName, cn.notSelectedOrganisation].join(
                          ' '
                        )
                      : cn.organisationName
                  }
                >
                  {org.description}
                </span>
                {currentUser?.organisationShortModel?.isLegal === false && (
                  <div
                    className={
                      isOrganisationSelected
                        ? `${cn.warningBanner} ${cn.organisationSelected}`
                        : cn.warningBanner
                    }
                  >
                    ⚠️ Организация не активирована
                  </div>
                )}
              </div>
            </div>
            {!hiddenRoles.some((r) => currentUser?.roles?.includes(r)) && (
              <div
                className={
                  org.code == organisation.code
                    ? [cn.segmentBlock, cn.org].join(' ')
                    : [cn.segmentBlock, cn.orgSelected].join(' ')
                }
              >
                <span>Сегмент:</span>
                <SegmentsSelect />
                {canEditSegment && (
                  <div className={cn.editOutlinedContainer}>
                    <EditOutlined />
                  </div>
                )}
              </div>
            )}
            <ToggleMenu
              menuItems={menuItems}
              current={currentTab}
              setCurrent={setCurrentTab}
            />
            {currentTab == 'Организация' && (
              <Data
                register={register}
                errors={errors}
                getValues={getValues}
                control={control}
                setValue={setValue}
              />
            )}
            {currentTab == 'Пользователи' && <Users />}
            {currentTab == 'Банковские счета' && <BankAccounts />}
            {currentTab == 'Договоры' && <Contracts />}
            {currentTab == 'Адреса доставки' && <DeliveryAddresses />}
            {currentTab == 'Отображение складов' && <StorePreferences />}
          </div>
        </section>
      ) : (
        <Loader isModalOpen={loading} loaderType="modal" />
      )}
    </>
  )
}

export default OrganisationSettings
